import React, {useEffect, useRef, useState} from 'react';
import {useController, useFormContext} from 'react-hook-form';
import clsx from 'clsx';
import {isValidPhoneNumber, CountryCode} from 'libphonenumber-js';
import {Trans} from '@ui/i18n/trans';
import {useTrans, UseTransReturn} from '@ui/i18n/use-trans';
import {ArrowDropDownIcon} from '@ui/icons/material/ArrowDropDown';
import {SearchIcon} from '@ui/icons/material/Search';
import {CheckCircleIcon} from '@ui/icons/material/CheckCircle';
import {CancelIcon} from '@ui/icons/material/Cancel';
import {
  COUNTRY_CALLING_CODES,
  CountryCallingCode,
  DEFAULT_COUNTRY,
} from './country-calling-codes';

interface Props {
  name: string;
  label?: React.ReactNode;
  required?: boolean;
  className?: string;
  // overrides the per-country example
  placeholder?: string;
}

function validatePhone(
  fullValue: string,
  country: CountryCallingCode,
  required: boolean,
  trans: UseTransReturn['trans'],
): true | string {
  const hasNumber = fullValue && fullValue !== country.dialCode;
  if (!hasNumber) {
    return required ? trans({message: 'This field is required'}) : true;
  }
  try {
    const valid = isValidPhoneNumber(fullValue, country.code as CountryCode);
    return valid
      ? true
      : trans({
          message: 'Invalid number for :country (:code)',
          values: {country: country.name, code: country.dialCode},
        });
  } catch {
    return trans({message: 'Invalid number'});
  }
}

// accept the number typed with the country code too (e.g. "258 84...")
function toNational(digits: string, country: CountryCallingCode): string {
  const code = country.dialCode.replace(/\D/g, '');
  return digits.startsWith(code) && digits.length - code.length >= 8
    ? digits.slice(code.length)
    : digits;
}

export function PhoneInputWithCountryCode({
  name,
  label,
  required = false,
  className,
  placeholder,
}: Props) {
  const {trans} = useTrans();
  const {trigger} = useFormContext();

  // ref keeps validate closure always seeing the latest country
  const selectedCountryRef = useRef<CountryCallingCode>(DEFAULT_COUNTRY);

  const {
    field,
    fieldState: {error},
  } = useController({
    name,
    rules: {
      validate: (value: string) =>
        validatePhone(value ?? '', selectedCountryRef.current, required, trans),
    },
  });

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Parse initial value (e.g., "+25884123456")
  const [selectedCountry, setSelectedCountry] = useState<CountryCallingCode>(
    () => {
      const val = field.value as string | undefined;
      if (val) {
        // longest match first to handle +1 (US/CA) correctly
        const match = [...COUNTRY_CALLING_CODES]
          .sort((a, b) => b.dialCode.length - a.dialCode.length)
          .find(c => val.startsWith(c.dialCode));
        if (match) return match;
      }
      return DEFAULT_COUNTRY;
    },
  );

  const [phoneNumber, setPhoneNumber] = useState<string>(() => {
    const val = field.value as string | undefined;
    if (val) {
      const match = [...COUNTRY_CALLING_CODES]
        .sort((a, b) => b.dialCode.length - a.dialCode.length)
        .find(c => val.startsWith(c.dialCode));
      if (match) return val.slice(match.dialCode.length);
    }
    return '';
  });

  // Keep ref in sync with state
  useEffect(() => {
    selectedCountryRef.current = selectedCountry;
  }, [selectedCountry]);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const filteredCountries = COUNTRY_CALLING_CODES.filter(c => {
    const q = search.toLowerCase();
    return (
      c.name.toLowerCase().includes(q) ||
      c.dialCode.includes(q) ||
      c.code.toLowerCase().includes(q)
    );
  });

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (
        !dropdownRef.current?.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setSearch('');
      }
    }
    if (isOpen) document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setSearch('');
        triggerRef.current?.focus();
      }
    }
    if (isOpen) document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  function selectCountry(country: CountryCallingCode) {
    setSelectedCountry(country);
    selectedCountryRef.current = country;
    const newValue = country.dialCode + toNational(phoneNumber, country);
    field.onChange(newValue);
    setIsOpen(false);
    setSearch('');
    // Re-validate immediately with the new country
    if (phoneNumber) trigger(name);
  }

  function handleNumberChange(e: React.ChangeEvent<HTMLInputElement>) {
    // Keep only digits and spaces for display, only digits for storage
    const raw = e.target.value;
    const digitsOnly = raw.replace(/\D/g, '');
    setPhoneNumber(digitsOnly);
    field.onChange(
      selectedCountry.dialCode + toNational(digitsOnly, selectedCountry),
    );
  }

  // Compute live validity for the indicator icon (only show when user has typed)
  const hasInput = phoneNumber.length > 0;
  const fullValue =
    selectedCountry.dialCode + toNational(phoneNumber, selectedCountry);
  let isCurrentlyValid: boolean | null = null;
  if (hasInput) {
    try {
      isCurrentlyValid = isValidPhoneNumber(
        fullValue,
        selectedCountry.code as CountryCode,
      );
    } catch {
      isCurrentlyValid = false;
    }
  }

  return (
    <div className={clsx('flex flex-col', className)}>
      {label !== undefined && (
        <span className="mb-4 block whitespace-nowrap text-left text-sm first-letter:capitalize">
          {label}
          {required && <span className="text-danger"> *</span>}
        </span>
      )}

      <div className="relative isolate flex items-stretch">
        {/* Country code trigger */}
        <div className="relative flex-shrink-0">
          <button
            ref={triggerRef}
            type="button"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-label={trans({message: 'Select country code'})}
            onClick={() => setIsOpen(v => !v)}
            className={clsx(
              'flex h-42 items-center gap-6 px-10 text-sm',
              'rounded-l-input border border-r-0 border-divider',
              'bg-transparent shadow-sm hover:bg-hover',
              'focus:border-primary/60 focus:outline-none focus:ring focus:ring-primary/focus',
              'select-none transition-shadow',
              error && 'border-danger',
            )}
          >
            <span className="text-xs font-medium tracking-tight">
              {selectedCountry.dialCode}
            </span>
            <ArrowDropDownIcon
              className={clsx(
                'text-muted transition-transform duration-150 icon-sm',
                isOpen && 'rotate-180',
              )}
            />
          </button>

          {/* Dropdown panel */}
          {isOpen && (
            <div
              ref={dropdownRef}
              role="listbox"
              aria-label={trans({message: 'Countries'})}
              className="absolute left-0 top-full z-50 mt-4 w-80 overflow-hidden rounded-md border border-divider bg-paper shadow-lg"
            >
              {/* Search */}
              <div className="flex items-center gap-8 border-b border-divider px-10 py-8">
                <SearchIcon className="flex-shrink-0 text-muted icon-sm" />
                <input
                  ref={searchRef}
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={trans({message: 'Search country or code...'})}
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="text-muted transition-colors hover:text-main"
                    tabIndex={-1}
                  >
                    ×
                  </button>
                )}
              </div>

              {/* Country list */}
              <ul
                className="max-h-56 overflow-y-auto overscroll-contain"
                role="group"
              >
                {filteredCountries.length === 0 ? (
                  <li className="px-12 py-10 text-center text-sm text-muted">
                    <Trans message="No results" />
                  </li>
                ) : (
                  filteredCountries.map(country => (
                    <li key={country.code} role="option">
                      <button
                        type="button"
                        onClick={() => selectCountry(country)}
                        className={clsx(
                          'flex w-full items-center gap-10 px-12 py-8 text-left text-sm',
                          'transition-colors hover:bg-hover',
                          selectedCountry.code === country.code &&
                            'bg-primary/focused font-medium',
                        )}
                      >
                        <span className="flex-1 truncate">
                          <Trans message={country.name} />
                        </span>
                        <span className="flex-shrink-0 font-mono text-xs text-muted">
                          {country.dialCode}
                        </span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Divider between selector and input */}
        <div
          className={clsx(
            'w-px flex-shrink-0 self-stretch bg-divider',
            error && 'bg-danger',
          )}
        />

        {/* Phone number input */}
        <input
          ref={field.ref}
          type="tel"
          name={field.name}
          value={phoneNumber}
          onChange={handleNumberChange}
          onBlur={field.onBlur}
          inputMode="numeric"
          placeholder={
            placeholder ?? selectedCountry.placeholder ?? '000 000 000'
          }
          className={clsx(
            'h-42 flex-1 pl-12 text-sm',
            'rounded-r-input border border-l-0 border-divider',
            'bg-transparent shadow-sm',
            'focus:border-primary/60 focus:outline-none focus:ring focus:ring-primary/focus',
            'transition-shadow',
            // make room for the validation icon
            isCurrentlyValid !== null ? 'pr-36' : 'pr-12',
            error && 'border-danger',
          )}
        />

        {/* Inline validation icon */}
        {hasInput && isCurrentlyValid !== null && (
          <span
            className={clsx(
              'pointer-events-none absolute right-12 top-1/2 -translate-y-1/2',
              isCurrentlyValid ? 'text-positive' : 'text-danger',
            )}
          >
            {isCurrentlyValid ? (
              <CheckCircleIcon className="icon-sm" />
            ) : (
              <CancelIcon className="icon-sm" />
            )}
          </span>
        )}
      </div>

      {/* Error or hint */}
      {error ? (
        <p className="pt-10 text-xs text-danger">{error.message as string}</p>
      ) : (
        hasInput &&
        isCurrentlyValid === false && (
          <p className="pt-10 text-xs text-muted">
            Formato inválido para {selectedCountry.name} (
            {selectedCountry.dialCode})
          </p>
        )
      )}
    </div>
  );
}
